import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { Appointment, Notification } from '../types';
import NavigationBar from '../components/NavigationBar';
import LoadingSpinner from '../components/LoadingSpinner';
import BusinessDashboardSidebar from '../components/BusinessDashboardSidebar';
import EditCustomerProfileModal from '../components/EditCustomerProfileModal';
import { ArrowLeft, Clock, ChevronRight, User as UserIcon, Pencil } from 'lucide-react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { getAppointmentStartIso, getServiceNames, getTotalDuration, getTotalPrice, getProfessionalNames } from '../utils/appointmentHelpers';
import { canViewFullCustomerPhone, isBusinessOwnerForBusiness } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';

interface Customer {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  createdAt: string;
  appointmentCount: number;
  lastAppointment?: {
    id: string;
    scheduledAt: string;
    status: string;
  } | null;
  nextAppointment?: {
    id: string;
    scheduledAt: string;
    status: string;
  } | null;
}

const CustomerDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const { customerId } = useParams<{ customerId: string }>();
  const { user } = useAuth();
  const { currentBusiness } = useBusiness();

  const canEditCustomer = useMemo(
    () => !!(user && currentBusiness?.id && isBusinessOwnerForBusiness(user, currentBusiness.id)),
    [user, currentBusiness?.id]
  );
  
  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [upcomingAppointments, setUpcomingAppointments] = useState<Appointment[]>([]);
  const [pastAppointments, setPastAppointments] = useState<Appointment[]>([]);
  const [appointmentStats, setAppointmentStats] = useState<{ noShows: number; lateCanceledRescheduled: number }>({
    noShows: 0,
    lateCanceledRescheduled: 0,
  });
  const [statsLoading, setStatsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Appointment filter and search
  const [appointmentFilter, setAppointmentFilter] = useState<'all' | 'upcoming' | 'past' | 'canceled'>('all');
  const [appointmentSearch, setAppointmentSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  
  // Pagination state
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [pastPage, setPastPage] = useState(1);
  const [upcomingPagination, setUpcomingPagination] = useState<any>(null);
  const [pastPagination, setPastPagination] = useState<any>(null);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  
  // Private notes state
  const [privateNotes, setPrivateNotes] = useState<string>('');
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesSaving, setNotesSaving] = useState(false);
  
  // Edit profile modal state
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState(false);
  
  // Customer invoices state
  const [customerInvoices, setCustomerInvoices] = useState<any[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  
  // Notification state for sidebar badge
  const [unreadNotifications, setUnreadNotifications] = useState<Notification[]>([]);
  
  // Load unread notifications count for badge
  useEffect(() => {
    const loadUnreadCount = async () => {
      if (!currentBusiness?.id) return;
      try {
        // Make a lightweight call with limit 1 just to get the total count from pagination
        const response = await apiService.getNotifications(currentBusiness.id, { page: 1, limit: 1, isRead: false });
        // Use pagination.total for the actual count, not the array length
        const totalUnread = response.pagination?.total || 0;
        // Set a dummy array with the count for compatibility, but we'll use totalUnread for display
        setUnreadNotifications(Array(totalUnread).fill(null) as Notification[]);
      } catch (error) {
        console.error('Error loading unread notifications count:', error);
        setUnreadNotifications([]);
      }
    };
    loadUnreadCount();
  }, [currentBusiness?.id]);

  // Handle notification bell click
  const handleNotificationsClick = () => {
    navigate('/dashboard/business?section=notifications');
  };

  useEffect(() => {
    const loadCustomerData = async () => {
      if (!customerId || !currentBusiness?.id) return;
      
      setLoading(true);
      setError(null);
      try {
        // Load customer info
        const customersResult = await apiService.getBusinessCustomers(currentBusiness.id, { limit: 1000 });
        const customerData = customersResult.data.find((c: { id: string }) => c.id === customerId);
        
        if (!customerData) {
          setError('Customer not found');
          setLoading(false);
          return;
        }
        
        setCustomer(customerData);
      } catch (error) {
        console.error('Error loading customer data:', error);
        setError('Failed to load customer data');
        toast.error('Failed to load customer data');
      } finally {
        setLoading(false);
      }
    };

    loadCustomerData();
  }, [customerId, currentBusiness?.id]);

  // Load visit stats (no-shows + late cancel/reschedule) for this customer
  useEffect(() => {
    const loadStats = async () => {
      if (!customerId || !currentBusiness?.id) return;
      setStatsLoading(true);
      try {
        const stats = await apiService.getAppointmentStats({
          businessId: currentBusiness.id,
          customerId,
        });
        setAppointmentStats({
          noShows: Number(stats?.noShows) || 0,
          lateCanceledRescheduled: Number(stats?.lateCanceledRescheduled) || 0,
        });
      } catch (error) {
        console.error('Error loading appointment stats:', error);
        // Don't block the page on stats
        setAppointmentStats({ noShows: 0, lateCanceledRescheduled: 0 });
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, [customerId, currentBusiness?.id]);

  // Load private notes
  useEffect(() => {
    const loadPrivateNotes = async () => {
      if (!customerId || !currentBusiness?.id) return;
      
      setNotesLoading(true);
      try {
        const notesData = await apiService.getCustomerPrivateNotes(customerId, currentBusiness.id);
        setPrivateNotes(notesData.notes || '');
      } catch (error) {
        console.error('Error loading private notes:', error);
        // Don't show error toast for notes, just set empty
        setPrivateNotes('');
      } finally {
        setNotesLoading(false);
      }
    };

    loadPrivateNotes();
  }, [customerId, currentBusiness?.id]);

  // Load customer invoices
  useEffect(() => {
    const loadInvoices = async () => {
      if (!customerId || !currentBusiness?.id) return;
      setInvoicesLoading(true);
      try {
        const result = await apiService.getInvoices({
          businessId: currentBusiness.id,
          customerId,
          limit: 100,
        });
        setCustomerInvoices(result.data ?? []);
      } catch (error) {
        console.error('Error loading customer invoices:', error);
        setCustomerInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    };
    loadInvoices();
  }, [customerId, currentBusiness?.id]);

  useEffect(() => {
    if (!canEditCustomer) {
      setIsEditingNotes(false);
      setIsEditProfileModalOpen(false);
    }
  }, [canEditCustomer]);

  // Handle save notes
  const handleSaveNotes = async () => {
    if (!canEditCustomer || !customerId || !currentBusiness?.id) return;
    
    setNotesSaving(true);
    try {
      await apiService.updateCustomerPrivateNotes(customerId, currentBusiness.id, privateNotes);
      setIsEditingNotes(false);
      toast.success('Private notes saved successfully');
    } catch (error) {
      console.error('Error saving private notes:', error);
      toast.error('Failed to save private notes');
    } finally {
      setNotesSaving(false);
    }
  };

  // Handle cancel editing
  const handleCancelEdit = async () => {
    // Reload original notes
    if (!customerId || !currentBusiness?.id) return;
    try {
      const notesData = await apiService.getCustomerPrivateNotes(customerId, currentBusiness.id);
      setPrivateNotes(notesData.notes || '');
    } catch (error) {
      console.error('Error loading private notes:', error);
    }
    setIsEditingNotes(false);
  };

  // Handle save customer profile
  const handleSaveCustomerProfile = async (data: { firstName: string; lastName: string; phone: string; notes: string }) => {
    if (!canEditCustomer || !customerId || !currentBusiness?.id) return;
    
    try {
      // Update customer profile (name and phone)
      // Send empty string to clear phone number, not undefined (undefined skips the update)
      const updatedCustomer = await apiService.updateCustomerProfile(customerId, currentBusiness.id, {
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone // Send empty string if phone is cleared
      });

      // Update private notes
      await apiService.updateCustomerPrivateNotes(customerId, currentBusiness.id, data.notes);

      // Update local state
      setCustomer(prev => prev ? {
        ...prev,
        firstName: updatedCustomer.firstName,
        lastName: updatedCustomer.lastName,
        phone: updatedCustomer.phone || undefined
      } : null);
      setPrivateNotes(data.notes);

      toast.success('Customer profile updated successfully');
    } catch (error: any) {
      console.error('Error updating customer profile:', error);
      toast.error(error?.response?.data?.message || 'Failed to update customer profile');
      throw error; // Re-throw to keep modal open on error
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(appointmentSearch);
    }, 500);
    return () => clearTimeout(timer);
  }, [appointmentSearch]);

  // Load appointments with server-side filtering and pagination
  const loadAppointments = async () => {
    if (!customerId || !currentBusiness?.id) return;
    
    setAppointmentsLoading(true);
    try {
      if (appointmentFilter === 'all') {
        // Load both upcoming and past appointments separately with proper pagination
        // Future appointments: any status (scheduledAt >= now)
        const upcomingParams: any = {
          businessId: currentBusiness.id,
          customerId: customerId,
          status: 'future',
          page: upcomingPage,
          limit: 10
        };
        if (debouncedSearch) {
          upcomingParams.search = debouncedSearch;
        }
        
        // Past appointments: any status (scheduledAt < now)
        const pastParams: any = {
          businessId: currentBusiness.id,
          customerId: customerId,
          status: 'past',
          page: pastPage,
          limit: 10,
          sort: 'desc',
        };
        if (debouncedSearch) {
          pastParams.search = debouncedSearch;
        }

        const [upcomingData, pastData] = await Promise.all([
          apiService.getAppointments(upcomingParams),
          apiService.getAppointments(pastParams),
        ]);

        setUpcomingAppointments(upcomingData?.data || []);
        setUpcomingPagination(upcomingData?.pagination);
        setPastAppointments(pastData?.data || []);
        setPastPagination(pastData?.pagination);
      } else {
        // Single filter - load appointments based on filter
        const params: any = {
          businessId: currentBusiness.id,
          customerId: customerId,
          page: appointmentFilter === 'upcoming' ? upcomingPage : pastPage,
          limit: 10
        };
        
        if (appointmentFilter === 'upcoming') {
          params.status = 'upcoming'; // Maps to PENDING and CONFIRMED
        } else if (appointmentFilter === 'past') {
          params.status = 'past';
          params.excludeCancelled = true;
          params.sort = 'desc';
        } else if (appointmentFilter === 'canceled') {
          params.status = 'CANCELLED';
          params.sort = 'desc';
        }
        
        if (debouncedSearch) {
          params.search = debouncedSearch;
        }
        
        const data = await apiService.getAppointments(params);
        
        if (appointmentFilter === 'upcoming') {
          setUpcomingAppointments(data.data || []);
          setUpcomingPagination(data.pagination);
          setPastAppointments([]);
          setPastPagination(null);
        } else {
          setPastAppointments(data.data || []);
          setPastPagination(data.pagination);
          setUpcomingAppointments([]);
          setUpcomingPagination(null);
        }
      }
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Failed to load appointments');
    } finally {
      setAppointmentsLoading(false);
    }
  };

  // Load appointments when filter, search, or pagination changes
  useEffect(() => {
    loadAppointments();
  }, [customerId, currentBusiness?.id, appointmentFilter, debouncedSearch, upcomingPage, pastPage]);

  // Reset pagination when filter or search changes
  useEffect(() => {
    setUpcomingPage(1);
    setPastPage(1);
  }, [appointmentFilter, debouncedSearch]);

  // Helper functions
  const formatCustomerEmail = (email: string | undefined | null): string => {
    if (!email) return 'No contact provided';
    if (email.startsWith('temp') && email.includes('@appointment.local')) {
      return 'No contact provided';
    }
    return email;
  };

  const formatPhoneNumber = (phone: string | undefined | null): string => {
    return displayCustomerPhone(phone, canViewFullCustomerPhone(user, currentBusiness?.id));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: '2-digit' });
  };

  const formatLongDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  /** Time string for appointment card: single time or "Between X – Y" for flex window */
  const getAppointmentTimeDisplay = (appointment: Appointment) => {
    const flex = (appointment as any).flexScheduledAt;
    if (flex?.earliest && flex?.latest) {
      return `Between ${formatTime(flex.earliest)} – ${formatTime(flex.latest)}`;
    }
    const iso = getAppointmentStartIso(appointment);
    return iso ? formatTime(iso) : '—';
  };

  const formatDateTimeHeader = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true
    });
    return { dateStr, timeStr };
  };

  const toInvoiceLabel = (id: string) => {
    const part = String(id).split('-')[0]?.toUpperCase() || 'INV';
    return `INV-${part}`;
  };

  const getDaysDifference = (dateString: string, isFuture: boolean = false) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffTime = date.getTime() - today.getTime();
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));
    
    if (isFuture) {
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day';
      return `${diffDays} days`;
    } else {
      const daysAgo = Math.abs(diffDays);
      if (daysAgo === 0) return 'Today';
      if (daysAgo === 1) return '1 day ago';
      return `${daysAgo} days ago`;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'CONFIRMED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'COMPLETED':
        return 'bg-[#0B4778]/20 text-[#0B4778] border-[#0B4778]/30';
      case 'CANCELLED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  const getStatusLabel = (status: string, noShow?: boolean) => {
    if (status === 'CANCELLED' && noShow) return 'No Show';
    switch (status) {
      case 'CONFIRMED':
        return 'Confirmed';
      case 'PENDING':
        return 'Pending';
      case 'COMPLETED':
        return 'Completed';
      case 'CANCELLED':
        return 'Canceled';
      default:
        return status;
    }
  };

  // Helper: resolved icon comes from the appointments API (service.icon -> category.icon -> null)
  const getAssignmentIcon = (assignment: Appointment['assignments'][number]): string | null => {
    const icon =
      (assignment as any)?.serviceIcon ||
      (assignment as any)?.icon ||
      (assignment as any)?.categoryIcon ||
      (assignment as any)?.category_icon ||
      (assignment as any)?.category?.icon;
    if (typeof icon === 'string' && icon.trim() !== '') return icon;
    return null;
  };

  // Helper to render pagination controls
  const renderPagination = (pagination: any, page: number, setPage: (page: number) => void, section: 'upcoming' | 'past') => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const hasPreviousPage = Boolean(pagination.hasPreviousPage ?? pagination.hasPrevPage);
    const hasNextPage = Boolean(pagination.hasNextPage);

    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, page - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(pagination.totalPages, startPage + maxPagesToShow - 1);
    
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          Showing {((page - 1) * pagination.limit) + 1} to {Math.min(page * pagination.limit, pagination.total)} of {pagination.total} appointments
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={!hasPreviousPage}
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
              hasPreviousPage
                ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            }`}
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {startPage > 1 && (
              <>
                <button
                  onClick={() => setPage(1)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  1
                </button>
                {startPage > 2 && <span className="text-gray-400">...</span>}
              </>
            )}
            {pageNumbers.map((num) => (
              <button
                key={num}
                onClick={() => setPage(num)}
                className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
                  num === page
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {num}
              </button>
            ))}
            {endPage < pagination.totalPages && (
              <>
                {endPage < pagination.totalPages - 1 && <span className="text-gray-400">...</span>}
                <button
                  onClick={() => setPage(pagination.totalPages)}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
                >
                  {pagination.totalPages}
                </button>
              </>
            )}
          </div>
          <button
            onClick={() => setPage(page + 1)}
            disabled={!hasNextPage}
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium ${
              hasNextPage
                ? 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar 
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={handleNotificationsClick}
        />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  if (error || !customer) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <NavigationBar 
          unreadNotificationsCount={unreadNotifications.length}
          onNotificationsClick={handleNotificationsClick}
        />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg font-medium text-gray-900 mb-2">{error || 'Customer not found'}</p>
            <button
              onClick={() => navigate('/dashboard/business?section=customers')}
              className="text-blue-600 hover:text-blue-800"
            >
              Back to Customers
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 no-animations flex flex-col">
      <NavigationBar 
        unreadNotificationsCount={unreadNotifications.length}
        onNotificationsClick={handleNotificationsClick}
      />
      
      <div className="flex-1 lg:grid lg:grid-cols-[auto_minmax(0,1fr)] min-w-0 w-full">
        {/* Mobile Menu Overlay */}
        {isSidebarOpen && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsSidebarOpen(false)}
          />
        )}
        
        {/* Sidebar */}
        <BusinessDashboardSidebar
          isSidebarOpen={isSidebarOpen}
          isSidebarCollapsed={isSidebarCollapsed}
          activeSection="customers"
          onClose={() => setIsSidebarOpen(false)}
          onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          unreadNotificationCount={unreadNotifications.length}
        />
        
        {/* Main Content */}
        <div className="flex flex-col flex-1 min-w-0 w-full">
          {/* Mobile Header */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <h1 className="flex-1 text-center text-lg font-semibold text-gray-900">Customer Details</h1>
              {/* Invisible spacer to balance hamburger button and center the title */}
              <div className="w-10 h-10"></div>
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-4 md:p-8 min-w-0 w-full max-w-full">
            {/* Header */}
            <div className="mb-6">
              <button
                onClick={() => navigate('/dashboard/business?section=customers')}
                className="text-gray-600 hover:text-gray-900 mb-4 flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Customers
              </button>
              <div className="flex items-center justify-between gap-4 mb-1">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">{customer.firstName} {customer.lastName}</h2>
                {canEditCustomer && (
                  <button 
                    onClick={() => setIsEditProfileModalOpen(true)}
                    className="aacore-btn2-secondary"
                  >
                    <i className="bi bi-pencil"></i>
                    Edit Profile
                  </button>
                )}
              </div>
              <div className="text-gray-600 space-y-1">
                <p className="flex items-center gap-2">
                  <i className="bi bi-envelope"></i>
                  {formatCustomerEmail(customer.email)}
                </p>
                {customer.phone && (
                  <p className="flex items-center gap-2">
                    <i className="bi bi-telephone"></i>
                    {formatPhoneNumber(customer.phone)}
                  </p>
                )}
                <p className="flex items-center gap-2">
                  <i className="bi bi-calendar3"></i>
                  Customer Since: <strong>{formatDate(customer.createdAt)}</strong>
                </p>
              </div>
            </div>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Total Visits Card */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="bi bi-calendar2-check text-blue-600 text-2xl"></i>
                    </div>
                    <div>
                      <div className="text-xs font-extrabold uppercase tracking-wider text-gray-600">Total Visits</div>
                      <div className="text-2xl font-bold text-gray-900 mt-1">{customer.appointmentCount}</div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-[11px] font-semibold text-gray-600 whitespace-nowrap">
                      No-Shows:{' '}
                      <span className="text-gray-900 font-bold">
                        {statsLoading ? '…' : appointmentStats.noShows}
                      </span>
                    </div>
                    <div className="text-[11px] font-semibold text-gray-600 whitespace-nowrap mt-1">
                      Rescheduled:{' '}
                      <span className="text-gray-900 font-bold">
                        {statsLoading ? '…' : appointmentStats.lateCanceledRescheduled}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Next Visit Card */}
              {customer.nextAppointment && (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-amber-50 border border-amber-200 rounded-lg flex flex-col items-center justify-center flex-shrink-0">
                        <div className="text-[8px] font-bold text-amber-600 uppercase leading-none">
                          {new Date(customer.nextAppointment.scheduledAt).toLocaleDateString('en-US', { weekday: 'short' })}
                        </div>
                        <div className="text-lg font-bold text-amber-600 leading-none mt-0.5">
                          {new Date(customer.nextAppointment.scheduledAt).getDate()}
                        </div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-extrabold uppercase tracking-wider text-gray-600">Next Visit</div>
                        <div className="text-base font-bold text-gray-900 mt-1">
                          {formatDate(customer.nextAppointment.scheduledAt)} at {formatTime(customer.nextAppointment.scheduledAt)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(customer.nextAppointment.status)}`}>
                        {getStatusLabel(customer.nextAppointment.status, (customer.nextAppointment as any)?.noShow ?? (customer.nextAppointment as any)?.no_show)}
                      </span>
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-gray-50 text-gray-700 border-gray-200">
                        {getDaysDifference(customer.nextAppointment.scheduledAt, true)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Private notes - full width on desktop */}
            <div className="w-full max-w-full mb-6">
              <div className="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-200">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs font-extrabold uppercase tracking-wider text-gray-600 mb-1">
                      PRIVATE NOTES
                    </div>
                    <div className="text-xs text-gray-600">
                      Visible only to staff with permission.
                    </div>
                  </div>
                  {canEditCustomer && !isEditingNotes && (
                    <button 
                      onClick={() => setIsEditingNotes(true)}
                      className="aacore-btn2-secondary"
                    >
                      <i className="bi bi-pencil"></i>
                      Edit
                    </button>
                  )}
                </div>
                {notesLoading ? (
                  <div className="text-sm text-gray-500 mt-2">Loading notes...</div>
                ) : isEditingNotes ? (
                  <div className="mt-2">
                    <textarea
                      value={privateNotes}
                      onChange={(e) => setPrivateNotes(e.target.value)}
                      placeholder="Enter private notes about this customer..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm text-gray-700 resize-none"
                      rows={4}
                    />
                    <div className="flex items-center gap-2 mt-3 sm:justify-center">
                      <button
                        onClick={handleCancelEdit}
                        disabled={notesSaving}
                        className="aacore-btn2-secondary disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
                      >
                        <i className="bi bi-x-lg"></i>
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveNotes}
                        disabled={notesSaving}
                        className="aacore-btn2-primary disabled:opacity-50 disabled:cursor-not-allowed flex-1 sm:flex-none"
                      >
                        <i className="bi bi-check-lg"></i>
                        {notesSaving ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-700 mt-2 whitespace-pre-wrap">
                    {privateNotes || (
                      <div className="flex items-start gap-2 text-amber-700">
                        <i className="bi bi-lock-fill mt-0.5 flex-shrink-0"></i>
                        <span>No private notes yet. Use this space for allergies, preferences, or VIP status.</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Appointment History (left) + Invoices (right) - equal height cards */}
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 min-w-0 w-full max-w-full items-stretch">
              {/* Left: Appointment History */}
              <div className="min-h-0 min-w-0 flex flex-col">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 flex flex-col min-h-0 h-full">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex-shrink-0">Appointment History</h3>
                  <div className="flex-1 min-h-0 overflow-auto flex flex-col min-w-0">
                  {/* Toolbar */}
                  <div className="flex flex-col gap-3 mb-4 min-w-0 flex-shrink-0">
                    {/* Filter Pills */}
                    <div className="min-w-0">
                      {/* Mobile/Tablet: Dropdown */}
                      <div className="lg:hidden">
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Filter</label>
                        <div className="relative">
                          <select
                            value={appointmentFilter}
                            onChange={(e) => setAppointmentFilter(e.target.value as any)}
                            className="w-full py-2 px-3 pr-10 border border-gray-300 rounded-lg bg-white text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="all">All</option>
                            <option value="upcoming">Upcoming</option>
                          <option value="past">Past</option>
                            <option value="canceled">Canceled</option>
                          </select>
                          <i className="bi bi-chevron-down pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                        </div>
                      </div>

                      {/* Desktop: Pills (no wrapping; search moves to next line on smaller widths) */}
                      <div className="hidden lg:flex min-w-0 flex-nowrap gap-2">
                        <button
                          onClick={() => setAppointmentFilter('all')}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap flex-shrink-0 transition-all ${
                            appointmentFilter === 'all'
                              ? 'bg-[#0B4778]/10 text-[#0B4778] border-[#0B4778]/30'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <i className="bi bi-inbox"></i>
                          All
                        </button>
                        <button
                          onClick={() => setAppointmentFilter('upcoming')}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap flex-shrink-0 transition-all ${
                            appointmentFilter === 'upcoming'
                              ? 'bg-amber-50 text-amber-700 border-amber-200'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <i className="bi bi-calendar-event"></i>
                          Upcoming
                        </button>
                        <button
                          onClick={() => setAppointmentFilter('past')}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap flex-shrink-0 transition-all ${
                            appointmentFilter === 'past'
                              ? 'bg-[#0B4778]/10 text-[#0B4778] border-[#0B4778]/30'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <i className="bi bi-check-circle"></i>
                          Past
                        </button>
                        <button
                          onClick={() => setAppointmentFilter('canceled')}
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-semibold border whitespace-nowrap flex-shrink-0 transition-all ${
                            appointmentFilter === 'canceled'
                              ? 'bg-gray-50 text-gray-700 border-gray-300'
                              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          <i className="bi bi-x-circle"></i>
                          Canceled
                        </button>
                      </div>
                    </div>
                    
                    {/* Search */}
                    <div className="relative w-full max-w-full">
                      <i className="bi bi-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                      <input
                        type="text"
                        placeholder="Search service or note…"
                        value={appointmentSearch}
                        onChange={(e) => setAppointmentSearch(e.target.value)}
                        className={`w-full py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${appointmentSearch ? 'pl-10 pr-10' : 'pl-10 pr-4'}`}
                      />
                      {appointmentSearch && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAppointmentSearch('');
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                          aria-label="Clear search"
                        >
                          <i className="bi bi-x text-xl"></i>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Appointment List */}
                  {appointmentsLoading ? (
                    <div className="text-center py-12">
                      <div className="text-gray-600">Loading appointments...</div>
                    </div>
                  ) : (appointmentFilter === 'all' 
                    ? (upcomingAppointments.length === 0 && pastAppointments.length === 0)
                    : (appointmentFilter === 'upcoming' 
                      ? upcomingAppointments.length === 0
                      : pastAppointments.length === 0)
                  ) ? (
                    <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
                      <i className="bi bi-calendar2-x text-red-600 text-2xl mb-2"></i>
                      <div className="font-semibold text-gray-900 mt-2">No matching appointments</div>
                      <div className="text-sm text-gray-600 mt-1">Adjust filters or search terms.</div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {/* Upcoming Section */}
                      {(appointmentFilter === 'all' || appointmentFilter === 'upcoming') && upcomingAppointments.length > 0 && (
                        <div>
                          <h4 className="text-xs font-extrabold uppercase tracking-wider text-gray-600 mb-2">Upcoming</h4>
                          <div className="space-y-2">
                            {upcomingAppointments.map((appointment) => {
                              const totalDuration = getTotalDuration(appointment);
                              const startIso = getAppointmentStartIso(appointment);
                              const startTime = startIso ? new Date(startIso) : null;
                              if (!startTime) return null;
                              const endTime = new Date(startTime.getTime() + totalDuration * 60000);
                              const endTimeStr = endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              return (
                                <div
                                  key={appointment.id}
                                  className="relative border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-inset focus-within:ring-[#0B4778]"
                                >
                                  <button
                                    type="button"
                                    className="absolute inset-0 z-10 w-full h-full rounded-lg focus:outline-none"
                                    onClick={() => navigate(`/appointments/${appointment.id}?from=customers`)}
                                    aria-label="View appointment"
                                  />
                                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-20" />
                                  <div className="relative min-w-0 pr-8">
                                    {/* Header: Date & Time */}
                                    <div className="mb-3">
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <div className="text-sm sm:text-base font-semibold text-gray-900 min-w-0">
                                            {getAppointmentStartIso(appointment) ? formatLongDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}
                                          </div>
                                          {appointment.bookingInitiator && (
                                            appointment.bookingInitiator === 'CUSTOMER' ? (
                                              <span
                                                className="inline-flex items-center justify-center border w-6 h-6 rounded-full bg-green-100 border-green-200 flex-shrink-0"
                                                title="Customer booked"
                                              >
                                                <UserIcon className="w-3 h-3 text-green-700" />
                                              </span>
                                            ) : (
                                              <span
                                                className="inline-flex items-center justify-center border w-6 h-6 rounded-md bg-orange-100 border-orange-200 flex-shrink-0"
                                                title="Booked by staff"
                                              >
                                                <Pencil className="w-3 h-3 text-orange-700" />
                                              </span>
                                            )
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-gray-50 text-gray-700 border-gray-200">
                                            {getAppointmentStartIso(appointment) ? getDaysDifference(getAppointmentStartIso(appointment)!, appointment.status === 'CONFIRMED' || appointment.status === 'PENDING') : '—'}
                                          </span>
                                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(appointment.status)}`}>
                                            {getStatusLabel(appointment.status, appointment.noShow ?? (appointment as any).no_show)}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="font-semibold flex items-center text-xs sm:text-sm" style={{ color: '#0B4778' }}>
                                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                                        {getAppointmentTimeDisplay(appointment)}
                                        {(appointment as any).flexScheduledAt?.earliest && (appointment as any).flexScheduledAt?.latest ? '' : totalDuration > 0 ? ` - ${endTimeStr}` : ''}
                                      </div>
                                    </div>

                                    {/* Services List */}
                                    {appointment.assignments && appointment.assignments.length > 0 && (
                                      <div className="border-t border-gray-100 pt-3 mb-3">
                                        {appointment.assignments.map((assignment, idx) => {
                                          const icon = getAssignmentIcon(assignment as any);
                                          const price = typeof assignment.price === 'number' 
                                            ? assignment.price 
                                            : parseFloat(assignment.price || '0') || 0;
                                          const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                            ? `${assignment.professionalFirstName} ${assignment.professionalLastName}`
                                            : null;
                                          return (
                                            <div key={idx} className="flex items-start justify-between py-1.5">
                                              <div className="flex items-start gap-2 flex-1">
                                                <span
                                                  className="w-6 h-6 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0"
                                                  style={{ backgroundColor: assignment.categoryBackgroundColor || undefined }}
                                                >
                                                  <span className="text-sm">{icon || '📦'}</span>
                                                </span>
                                                <div className="flex flex-col">
                                                  <span className="text-sm text-gray-700">
                                                    {assignment.serviceName || 'Unknown Service'}
                                                  </span>
                                                  {professionalName && (
                                                    <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                                      <i className="bi bi-person-badge text-gray-400"></i> 
                                                      {assignment.professionalFirstName} {assignment.professionalLastName?.charAt(0)}.
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                              <span className="text-sm font-medium text-gray-900 flex-shrink-0">${price.toFixed(2)}</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {/* Total + Invoice badge */}
                                    <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                      <div className="flex items-center">
                                        {appointment.invoiceId && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.preventDefault();
                                              e.stopPropagation();
                                              navigate(`/dashboard/business/invoices/${appointment.invoiceId}`);
                                            }}
                                            className="relative z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors"
                                            title="View invoice"
                                          >
                                            <span aria-hidden>→</span>
                                            Invoice ({appointment.invoiceStatus || 'DRAFT'})
                                          </button>
                                        )}
                                      </div>
                                      <span className="text-base font-bold text-gray-900">Total: ${getTotalPrice(appointment).toFixed(2)}</span>
                                    </div>

                                    {/* Notes */}
                                    {appointment.notes && (
                                      <div className="mt-3 pt-3 border-t border-gray-100">
                                        <div className="text-sm text-gray-700 flex items-start gap-2">
                                          <i className="bi bi-chat-left-text text-amber-600 mt-0.5"></i>
                                          <span><span className="text-gray-600">Note:</span> <span className="text-gray-900">{appointment.notes}</span></span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Upcoming Pagination */}
                          {appointmentFilter === 'all' && renderPagination(upcomingPagination, upcomingPage, setUpcomingPage, 'upcoming')}
                        </div>
                      )}

                      {/* Past Section */}
                      {(appointmentFilter === 'all' || appointmentFilter === 'past' || appointmentFilter === 'canceled') && pastAppointments.length > 0 && (
                        <div>
                          <h4 className="text-xs font-extrabold uppercase tracking-wider text-gray-600 mb-2 mt-4">
                            {appointmentFilter === 'all' ? 'Past' : ''}
                          </h4>
                          <div className="space-y-2">
                            {pastAppointments.map((appointment) => {
                              const totalDuration = getTotalDuration(appointment);
                              const startIso = getAppointmentStartIso(appointment);
                              const startTime = startIso ? new Date(startIso) : null;
                              if (!startTime) return null;
                              const endTime = new Date(startTime.getTime() + totalDuration * 60000);
                              const endTimeStr = endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                              return (
                                <div
                                  key={appointment.id}
                                  className="relative border border-gray-200 rounded-lg p-4 bg-white shadow-sm hover:shadow-sm transition-shadow focus-within:ring-2 focus-within:ring-inset focus-within:ring-[#0B4778]"
                                >
                                  <button
                                    type="button"
                                    className="absolute inset-0 z-10 w-full h-full rounded-lg focus:outline-none"
                                    onClick={() => navigate(`/appointments/${appointment.id}?from=customers`)}
                                    aria-label="View appointment"
                                  />
                                  <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-20" />
                                  <div className="relative min-w-0 pr-8">
                                    {/* Header: Date & Time */}
                                    <div className="mb-3">
                                      <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className="flex items-center gap-2 min-w-0">
                                          <div className="text-sm sm:text-base font-semibold text-gray-900 min-w-0">
                                            {getAppointmentStartIso(appointment) ? formatLongDate(getAppointmentStartIso(appointment)!) : 'Date TBD'}
                                          </div>
                                          {appointment.bookingInitiator && (
                                            appointment.bookingInitiator === 'CUSTOMER' ? (
                                              <span
                                                className="inline-flex items-center justify-center border w-6 h-6 rounded-full bg-green-100 border-green-200 flex-shrink-0"
                                                title="Customer booked"
                                              >
                                                <UserIcon className="w-3 h-3 text-green-700" />
                                              </span>
                                            ) : (
                                              <span
                                                className="inline-flex items-center justify-center border w-6 h-6 rounded-md bg-orange-100 border-orange-200 flex-shrink-0"
                                                title="Booked by staff"
                                              >
                                                <Pencil className="w-3 h-3 text-orange-700" />
                                              </span>
                                            )
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-gray-50 text-gray-700 border-gray-200">
                                            {getAppointmentStartIso(appointment) ? getDaysDifference(getAppointmentStartIso(appointment)!, false) : '—'}
                                          </span>
                                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusColor(appointment.status)}`}>
                                            {getStatusLabel(appointment.status, appointment.noShow ?? (appointment as any).no_show)}
                                          </span>
                                        </div>
                                      </div>
                                      <div className="font-semibold flex items-center text-xs sm:text-sm" style={{ color: '#0B4778' }}>
                                        <Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 flex-shrink-0" />
                                        {getAppointmentTimeDisplay(appointment)}
                                        {(appointment as any).flexScheduledAt?.earliest && (appointment as any).flexScheduledAt?.latest ? '' : totalDuration > 0 ? ` - ${endTimeStr}` : ''}
                                      </div>
                                    </div>

                                    {/* Services List */}
                                      {appointment.assignments && appointment.assignments.length > 0 && (
                                        <div className="border-t border-gray-100 pt-3 mb-3">
                                          {appointment.assignments.map((assignment, idx) => {
                                            const icon = getAssignmentIcon(assignment as any);
                                            const price = typeof assignment.price === 'number' 
                                              ? assignment.price 
                                              : parseFloat(assignment.price || '0') || 0;
                                            const professionalName = assignment.professionalFirstName && assignment.professionalLastName
                                              ? `${assignment.professionalFirstName} ${assignment.professionalLastName}`
                                              : null;
                                            return (
                                              <div key={idx} className="flex items-start justify-between py-1.5">
                                                <div className="flex items-start gap-2 flex-1">
                                                  <span
                                                    className="w-6 h-6 rounded-md bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0"
                                                    style={{ backgroundColor: assignment.categoryBackgroundColor || undefined }}
                                                  >
                                                    <span className="text-sm">{icon || '📦'}</span>
                                                  </span>
                                                  <div className="flex flex-col">
                                                    <span className="text-sm text-gray-700">
                                                      {assignment.serviceName || 'Unknown Service'}
                                                    </span>
                                                    {professionalName && (
                                                      <span className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                                                        <i className="bi bi-person-badge text-gray-400"></i> 
                                                        {assignment.professionalFirstName} {assignment.professionalLastName?.charAt(0)}.
                                                      </span>
                                                    )}
                                                  </div>
                                                </div>
                                                <span className="text-sm font-medium text-gray-900 flex-shrink-0">${price.toFixed(2)}</span>
                                              </div>
                                            );
                                          })}
                                        </div>
                                      )}

                                      {/* Total + Invoice badge */}
                                      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                                        <div className="flex items-center">
                                          {appointment.invoiceId && (
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                navigate(`/dashboard/business/invoices/${appointment.invoiceId}`);
                                              }}
                                              className="relative z-20 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100 transition-colors"
                                              title="View invoice"
                                            >
                                              <span aria-hidden>→</span>
                                              Invoice ({appointment.invoiceStatus || 'DRAFT'})
                                            </button>
                                          )}
                                        </div>
                                        <span className="text-base font-bold text-gray-900">Total: ${getTotalPrice(appointment).toFixed(2)}</span>
                                      </div>

                                      {/* Notes */}
                                      {appointment.notes && (
                                        <div className="mt-3 pt-3 border-t border-gray-100">
                                          <div className="text-sm text-gray-700 flex items-start gap-2">
                                            <i className="bi bi-chat-left-text text-amber-600 mt-0.5"></i>
                                            <span><span className="text-gray-600">Note:</span> <span className="text-gray-900">{appointment.notes}</span></span>
                                          </div>
                                        </div>
                                      )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                          {/* Past Pagination */}
                          {renderPagination(pastPagination, pastPage, setPastPage, 'past')}
                        </div>
                      )}
                    </div>
                  )}
                  </div>
                </div>
              </div>

              {/* Right: Invoices */}
              <div className="min-h-0 min-w-0 flex flex-col">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 flex-1 flex flex-col min-h-0 h-full">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex-shrink-0">Invoices</h3>
                  <div className="flex-1 min-h-0 overflow-auto">
                    {invoicesLoading ? (
                      <div className="text-center py-8 text-gray-600">Loading invoices…</div>
                    ) : customerInvoices.length === 0 ? (
                      <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                        <i className="bi bi-receipt text-gray-400 text-2xl mb-2"></i>
                        <div className="font-semibold text-gray-900 mt-2">No invoices</div>
                        <div className="text-sm text-gray-600 mt-1">Invoices for this customer will appear here.</div>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {customerInvoices.map((inv: any) => {
                          const total = typeof inv.total === 'number' ? inv.total : parseFloat(inv.total) || 0;
                          const status = String(inv.status || 'DRAFT').toUpperCase();
                          const statusLabel = status.charAt(0) + status.slice(1).toLowerCase();
                          const statusClass = status === 'PAID' ? 'bg-green-50 text-green-700 border-green-200' : status === 'SENT' ? 'bg-blue-50 text-blue-700 border-blue-200' : status === 'VOIDED' ? 'bg-gray-100 text-gray-600 border-gray-200' : 'bg-amber-50 text-amber-700 border-amber-200';
                          return (
                            <div
                              key={inv.id}
                              className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition-colors"
                            >
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <button
                                  type="button"
                                  onClick={() => navigate(`/dashboard/business/invoices/${inv.id}`)}
                                  className="text-left font-semibold text-gray-900 hover:underline truncate min-w-0"
                                >
                                  {toInvoiceLabel(inv.id)}
                                </button>
                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${statusClass}`}>
                                    Status: {statusLabel}
                                  </span>
                                  <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border bg-gray-50 text-gray-900 border-gray-200">
                                    ${total.toFixed(2)}
                                  </span>
                                </div>
                              </div>
                              <div className="mt-2 text-sm text-gray-600">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-0">
                                  <span><span className="text-gray-500">Invoice date:</span> {formatDate(inv.invoice_date || inv.created_at || '')}</span>
                                  <i className="bi bi-dot text-gray-400" aria-hidden />
                                  <span><span className="text-gray-500">Due date:</span> {formatDate(inv.due_date || inv.invoice_date || inv.created_at || '')}</span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Customer Profile Modal */}
      {customer && canEditCustomer && (
        <EditCustomerProfileModal
          isOpen={isEditProfileModalOpen}
          onClose={() => setIsEditProfileModalOpen(false)}
          onSave={handleSaveCustomerProfile}
          customer={{
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone || null
          }}
          privateNotes={privateNotes}
        />
      )}
    </div>
  );
};

export default CustomerDetailsPage;
